import "./App.css";

import React, { useEffect } from "react";
import { Route, Routes } from "react-router-dom";

// Third-party
import Lottie from "lottie-react";
import { Global } from "@emotion/react";
import styled from "@emotion/styled";

// Contexts & Store
import { ChattingRoomProvider } from "./contexts/ChattingRoomContext";
import { SendBirdChatProvider } from "./contexts/SendBirdChatContext";
import { useTokenStore } from "./store/TokenStore";

// Assets & Styles
import loading from "./assets/Loading.json";
import reset from "@src/styles/Reset";

// Components
import Login from "./components/Login";
import AllFilter_search from "./components/filter/AllFilter_search";
import CustomerServiceInquiry from "./components/customerService/inquiry";
import IntrenalTablePage from "./components/internalTable/InternalTablePage";
import TestAiPage from "./components/aiFilter/TestAiPage";
import TestPage from "./components/table/TestPage";

// Pages - Auth & Signup
import LoginBox from "./pages/LoginBox";
import SignUpBox from "./pages/SignUpBox";
import SignUpExpert from "./pages/signupExpert";
import UserTypeSelect from "./pages/signupExpert/UserTypeSelect";
import OfficeSearch from "./pages/signupExpert/OfficeSearch";
import LocationSearch from "./pages/signupExpert/LocationSearch";
import WriteOffice from "./pages/signupExpert/WriteOffice";
import UserInfoOfficeSearch from "./pages/signupExpert/UserInfoOfficeSearch";

// Pages - Map & Search
import Map from "@src/pages/map/Map";
import MapFilter from "./pages/mapFilter";
import AllFilter from "./pages/mapFilter/AllFilter";
import RealPriceFilter from "./pages/map/RealPrice/RealPriceFilter";
import { SearchItems } from "./pages/searchItems";
import FavoriteRegion from "./pages/favoriteRegion";
import DailyNewAnnounce from "./pages/DailyNewAnnounce";
import DepositDetail from "./pages/DepositDetail";
import FieldDetail from "./pages/fieldDetail";
import PanoramaView from "./pages/panorama";

// Pages - AI Filter
import AiRecommend from "./pages/aiRecommend";
import AiFilterMainV2 from "./pages/aiFilter/AiFilterMainV2";
import AiFilterLocationV2 from "./pages/aiFilter/AiFilterLocationV2";
import AiFilterPurposeV2 from "./pages/aiFilter/AiFilterPurposeV2";

// Pages - Deungi (등기부등본)
import DeungiMain from "./pages/deungi/DeungiMain";
import DeungiPayment from "./pages/deungi/DeungiPayment";
import DeungiList from "./pages/deungi/DeungiList";
import PdfViewer from "./pages/deungi/PdfViewer";
import CharginUseList from "./pages/deungi/CharginUseList";
import DeungiTicketCancelList from "./pages/deungi/DeungiTicketCancelList";
import DeungiTicketRefund from "./pages/deungi/deingiTicketRefund";
import DeungiAlarmMain from "./pages/deungi/DeungiAlarmMain";
import DeungiAlarmSetting from "./pages/deungi/DeungiAlarmSetting";
import DeungiAlarmSearch from "./pages/deungi/DeungiAlarmSearch";
import DeungiAlarmDetail from "./pages/deungi/DeungiAlarmDetail";
import DeungiAlarmSubmitList from "./pages/deungi/DeungiAlarmSubmitList";

// Pages - Corp Deungi (법인등기)
import CorpDeungiMain from "./pages/corpDeungi/CorpDeungiMain";
import CorpDeungiPayment from "./pages/corpDeungi/CorpDeungiPayment";
import CorpPdfViewer from "./pages/corpDeungi/CorpPdfViewer";
import CorpDeungiTicketRefund from "./pages/corpDeungi/CorpDeungiTicketRefund";

// Pages - Building Register (건축물대장)
import BuildingRegisterMain from "./pages/buildingRegister/BuildingRegisterMain";
import BuildingRegisterList from "./pages/buildingRegister/BuildingRegisterList";
import BuildingRegisterPdfViewer from "./pages/buildingRegister/BuildingRegisterPdfViewer";
import BuildingRegisterPayment from "./pages/buildingRegister/BuildingRegisterPayment";

// Pages - Land Register (토지·임야대장)
import LandRegisterMain from "./pages/landRegister/LandRegisterMain";
import LandRegisterIssuedList from "./pages/landRegister/LandRegisterIssuedList";
import LandRegisterPdfViewer from "./pages/landRegister/LandRegisterPdfViewer";
import LandRegisterIssueStatusWatch from "./pages/landRegister/LandRegisterIssueStatusWatch";
import LandRegisterIssueWaiting from "./pages/landRegister/LandRegisterIssueWaiting";
import LandRegisterPayment from "./pages/landRegister/LandRegisterPayment";

// Pages - Campus
import Campus from "./pages/campus/Campus";
import CampusDetail from "./pages/campus/campusDetail/CampusDetail";
import CampuseRefundV2 from "./pages/campus/CampussRefundV2";
import Sugang from "./pages/campus/sugang/Sugang";
import WriteGrade from "./pages/campus/sugang/WriteGrade";

// Pages - Expert (전문가)
import ExpertInfos from "./pages/expert/ExpertInfos";
import ExpertApplication from "./pages/expert/ExpertApplication";
import ExpertAdList from "./pages/expert/AdItemList";
import Inquiry from "./pages/expert/Inquiry/Inquiry";
import { ExpertLocalAdIntroductionsPopup } from "./pages/expert/RegionAd";
import ExpertServiceNotice from "./pages/expert/service/ExpertServiceNotice";
import ExpertAgentPayment from "./pages/expert/service/ExpertAgentPayment";
import ExpertConsultingPayment from "./pages/expert/service/ExpertConsultingPayment";
import ExpertCompanionPayment from "./pages/expert/service/ExpertCompanionPayment";
import ExpertMatchPricePayment from "./pages/expert/service/ExpertMatchPricePayment";
import RegionAdsRefund from "./pages/expert/RegionAd/Refund";
import ExpertSearch from "./pages/expertSearch";

// Pages - Advertisement (광고)
import AdvertisementPayment from "./pages/advertisement/AdvertisementPayment";
import AdvertisementLocalSeleter from "./pages/advertisement/AdvertisementLocalSeleter";
import AdvertisementCancelRefund from "./pages/advertisement/AdvertisementCancelRefund";
import AdvertisementList from "./pages/advertisement/AdvertisementList";
import AdvertisementFilter from "./pages/advertisement/AdvertisementFilter";
import AdvertisementInfoRegion from "./pages/advertisement/AdvertisementInfoRegion";
import AdvertisementInfoItem from "./pages/advertisement/AdvertisementInfoItem";

// Pages - Architect Advertisement (건축사 광고)
import ArchitectInformation from "./pages/advertisementArchitect/ArchitectInformation";
import ArchitectSeleter from "./pages/advertisementArchitect/ArchitectSeleter";
import ArchitectPayment from "./pages/advertisementArchitect/ArchitectPayment";
import ArchitectList from "./pages/advertisementArchitect/ArchitectList";

// Pages - GoStop
import GoStop from "./pages/goStop";
import GoStopList from "./pages/goStop/GoStopList";
import GoStopDialog from "./pages/goStop/GoStopDialog";
import StoreGoStop from "./pages/storeGoStop";
import StoreGoStopList from "./pages/storeGoStop/StoreGoStopList";
import StoreGoStopCalculate from "./pages/storeGoStop/StoreGoStopCalculate";
import StoreGoStopDialog from "./pages/goStop/StoreGoStopDialog";
import ServiceBannerPage from "./pages/storeGoStop/ServiceBannerPage";

// Pages - Subscription & Refund
import SubscriptionInfo from "./pages/subscription";
import IosSubscriptionInfo from "./pages/subscription/iosIndex";
import UnSubscribe from "./pages/unSubscription";
import UnsubsribeRefund from "./pages/unSubscription/UnsubsribeRefund";
import UnSubscriptionWithDraw from "./pages/unSubscription/UnSubscriptionWithDraw";

// Pages - State Land (국유지)
import StateLandInfo from "./pages/stateLand/stateLandInfo";
import StateLandRefund from "./pages/stateLand/StateLandRefund";
import StateLandUserFilter from "./pages/stateLandFilter/StateLandUserFilter";

// Pages - Owner Check / Coupon
import OwnerCheckChargePayment from "./pages/ownerCheck/ChargePayment";
import CouponRegistrationCompleted from "./pages/coupon/CouponRegistrationCompleted";
import RegisterCouponIOS from "./pages/coupon/RegisterCouponIOS";

// Pages - MissgoAI
import MissgoAI from "./pages/MissgoAI";
import MissgoAIPdfViewer from "./pages/MissgoAI/components/MissgoAIGuide/optionUtils/DataTable/MissgoAIPdfViewer";
import MissgoAIDeep from "./pages/MissgoAI/MissgoAIDeep";
import MissgoAIDeepPayment from "./pages/MissgoAI/MissgoAIDeepPayment";

// Pages - Events
import AiChatBotBannerEvent from "./pages/event/AiChatBotBannerEvent";
import AiDividendEvent from "./pages/event/aiDividendEvent";
import AdvertisementEvent from "./pages/event/advertisementEvent";
import ArchitectEvent from "./pages/event/architectEvent";
import EarlyBirdVip50Event from "./pages/event/earlyBirdVip50";
import FiveFreeEvent from "./pages/event/FiveFreeEvent";
import MissgoAiBannerEvent from "./pages/event/missgoAiBannerEvent";
import OwnerCheckServiceEvent from "./pages/event/ownerCheckServiceEvent";

// Pages - Others
import AuctionAgentChatting from "./pages/auctionAgent/chatting";
import BuildingStatusTablePage from "./pages/BuildingStatusTable";
import DatamapNewFnEvent from "./pages/datamapNewFnEvent/DatamapNewFnEvent";
import Error from "./pages/Error";
import IosDatamap from "./pages/introduction/IosDatamap";
import RoadPlanRefund from "./pages/roadPlan/RoadPlanRefund";
import SampleBidList from "./pages/sampleBidList/SampleBidList";
import SmallRentTablePage from "./pages/smallRentTable";
import SpeechShorts from "./pages/SpeechShorts";
import StatusItem from "./pages/StatusItemList/StatusItemList";
import StoreRedirect from "./pages/redirectStore";
import ThreePage from "./pages/three";
import Accompanying from "./pages/event/accompanying";

function Router() {
    const tokenStore = useTokenStore((state) => state.accessToken);

    useEffect(() => {
        if (
            !(window.navigator.userAgent.includes("Android") || window.navigator.userAgent.includes("iPhone")) &&
            !tokenStore.length &&
            (window.location.search.includes("pnu") || window.location.search.includes("itemId"))
        ) {
            const langingLink = document.createElement("a");
            document.body.appendChild(langingLink);
            langingLink.href = "https://www.missgoauction.com";
            langingLink.click();
            document.body.removeChild(langingLink);
        }
    }, []);

    return (
        <>
            <Global styles={reset} />
            <Routes>
                {/* Auth & Signup */}
                <Route path="/login" element={<LoginBox />} />
                <Route path="/userType" element={<UserTypeSelect />} />
                <Route path="/signup" element={<SignUpBox />} />
                <Route path="/home" element={<Login />} />
                <Route path="/signup/expert" element={<SignUpExpert />} />
                <Route path="/signup/officeSearch" element={<OfficeSearch />} />
                <Route path="/signup/locationSearch" element={<LocationSearch />} />
                <Route path="/signup/writeOffice" element={<WriteOffice />} />

                {tokenStore?.length > 0 ? (
                    <>
                        {/* Error */}
                        <Route path="/error" element={<Error />} />

                        {/* Map & Search */}
                        <Route path="/map" element={<Map />} />
                        <Route path="/mapFilter" element={<MapFilter />} />
                        <Route path="/allFilter/map" element={<AllFilter />} />
                        <Route path="/allFilter/search" element={<AllFilter_search />} />
                        <Route path="/realPriceFilter" element={<RealPriceFilter />} />
                        <Route path="/searchItems" element={<SearchItems />} />
                        <Route path="/items/:id/deposit-detail" element={<DepositDetail />} />
                        <Route path="/items/daily-new" element={<DailyNewAnnounce />} />
                        <Route path="/favorite-region" element={<FavoriteRegion />} />
                        <Route path="/favoriteRegion" element={<FavoriteRegion />} />
                        <Route path="/fieldDetail" element={<FieldDetail />} />
                        <Route path="/panoramaView" element={<PanoramaView />} />

                        {/* AI Filter */}
                        <Route path="/aiFilterV2" element={<AiRecommend />} />
                        <Route path="/aiFilterV2/locationV2" element={<AiRecommend preStep={2} />} />
                        <Route path="/aiFilterv2/locationv2/purposev2" element={<AiRecommend preStep={3} />} />
                        <Route path="/smallFilter" element={<AiFilterMainV2 />} />
                        <Route path="/smallFilter/locationV2" element={<AiFilterLocationV2 />} />
                        <Route path="/smallFilter/locationv2/purposev2" element={<AiFilterPurposeV2 />} />

                        {/* Deungi (등기부등본) */}
                        <Route path="/deungi" element={<DeungiMain />} />
                        <Route path="/deungi/payment" element={<DeungiPayment />} />
                        <Route path="/deungi/pdf" element={<PdfViewer />} />
                        <Route path="/deungi/list" element={<DeungiList />} />
                        <Route path="/deungi/charginguselist" element={<CharginUseList />} />
                        <Route path="/deungi/refund" element={<DeungiTicketCancelList />} />
                        <Route path="/deungi/readingticketcancel" element={<DeungiTicketRefund />} />
                        <Route path="/deungi/alarm" element={<DeungiAlarmMain />} />
                        <Route path="/deungi/alarm/setting" element={<DeungiAlarmSetting />} />
                        <Route path="/deungi/alarm/search" element={<DeungiAlarmSearch />} />
                        <Route path="/deungi/alarm/detail" element={<DeungiAlarmDetail />} />
                        <Route path="/deungi/alarm/list" element={<DeungiAlarmSubmitList />} />

                        {/* Corp Deungi (법인등기) */}
                        <Route path="/corpDeungi" element={<CorpDeungiMain />} />
                        <Route path="/corpDeungi/payment" element={<CorpDeungiPayment />} />
                        <Route path="/corpDeungi/pdf" element={<CorpPdfViewer />} />
                        <Route path="/corpDeungi/readingticketcancel" element={<CorpDeungiTicketRefund />} />

                        {/* Building Register (건축물대장) */}
                        <Route path="/buildingRegister" element={<BuildingRegisterMain />} />
                        <Route path="/buildingRegister/list" element={<BuildingRegisterList />} />
                        <Route path="/buildingRegister/viewer" element={<BuildingRegisterPdfViewer />} />
                        <Route path="/buildingRegister/payment" element={<BuildingRegisterPayment />} />

                        {/* Land Register (토지·임야대장) */}
                        <Route path="/landRegister" element={<LandRegisterMain />} />
                        <Route path="/landRegister/list" element={<LandRegisterIssuedList />} />
                        <Route path="/landRegister/viewer" element={<LandRegisterPdfViewer />} />
                        <Route path="/landRegister/status" element={<LandRegisterIssueStatusWatch />} />
                        <Route path="/landRegister/issue" element={<LandRegisterIssueWaiting />} />
                        <Route path="/landRegister/payment" element={<LandRegisterPayment />} />

                        {/* Campus */}
                        <Route path="/campus" element={<Campus />} />
                        <Route path="/campus/:path" element={<Campus />} />
                        <Route path="/campus/detail/:campusNo" element={<CampusDetail />} />
                        <Route path="/campus/sugang/:campusNo" element={<Sugang />} />
                        <Route path="/campus/sugang/grade/:campusNo" element={<WriteGrade />} />
                        <Route path="/campus/refund" element={<CampuseRefundV2 />} />

                        {/* Expert (전문가) */}
                        <Route path="/expert/inquiry" element={<Inquiry />} />
                        <Route path="/expert/localAd/introduction/popup" element={<ExpertLocalAdIntroductionsPopup />} />
                        <Route path="/expert/info" element={<ExpertInfos />} />
                        <Route path="/expert/application" element={<ExpertApplication />} />
                        <Route path="/expert/officeSearch" element={<UserInfoOfficeSearch />} />
                        <Route path="/expert/serviceNotice" element={<ExpertServiceNotice />} />
                        <Route path="/expert/payment/agent" element={<ExpertAgentPayment />} />
                        <Route path="/expert/payment/consulting" element={<ExpertConsultingPayment />} />
                        <Route path="/expert/payment/companion" element={<ExpertCompanionPayment />} />
                        <Route path="/expert/payment/matchPrice" element={<ExpertMatchPricePayment />} />
                        <Route path="/expertAdList" element={<ExpertAdList />} />
                        <Route path="/expertSearch" element={<ExpertSearch />} />

                        {/* Advertisement (광고) */}
                        <Route path="/advertisement/payment" element={<AdvertisementPayment />} />
                        <Route path="/advertisement/selecter" element={<AdvertisementLocalSeleter />} />
                        <Route path="/advertisement/list" element={<AdvertisementList />} />
                        <Route path="/advertisement/filter" element={<AdvertisementFilter />} />
                        <Route path="/advertisement/info/region" element={<AdvertisementInfoRegion />} />
                        <Route path="/advertisement/info/item" element={<AdvertisementInfoItem />} />
                        <Route path="/advertisementCancelRefund" element={<AdvertisementCancelRefund />} />

                        {/* Architect Advertisement (건축사 광고) */}
                        <Route path="/advertisement-architect/info" element={<ArchitectInformation />} />
                        <Route path="/advertisement-architect/selecter" element={<ArchitectSeleter />} />
                        <Route path="/advertisement-architect/payment" element={<ArchitectPayment />} />
                        <Route path="/advertisement-architect/list" element={<ArchitectList />} />

                        {/* GoStop */}
                        <Route path="/goStop" element={<GoStop />} />
                        <Route path="/goStopList" element={<GoStopList />} />
                        <Route path="/goStopRefund" element={<GoStopDialog />} />
                        <Route path="/storeGoStop" element={<StoreGoStop />} />
                        <Route path="/storeGoStopList" element={<StoreGoStopList />} />
                        <Route path="/storeGoStop/calculate" element={<StoreGoStopCalculate />} />
                        <Route path="/storeGoStopRefund" element={<StoreGoStopDialog />} />
                        <Route path="/storeGoStop/introduction" element={<ServiceBannerPage />} />

                        {/* Subscription & Refund */}
                        <Route path="/subscriptionInfo" element={<SubscriptionInfo />} />
                        <Route path="/ios-subscriptionInfo" element={<IosSubscriptionInfo />} />
                        <Route path="/unSubscribe" element={<UnSubscribe />} />
                        <Route path="/unSubscribe/refund" element={<UnsubsribeRefund />} />
                        <Route path="/unSubscribe/withDraw" element={<UnSubscriptionWithDraw />} />
                        <Route path="/regionAdsRefund" element={<RegionAdsRefund />} />
                        <Route path="/stateLandRefund" element={<StateLandRefund />} />
                        <Route path="/roadPlanRefund" element={<RoadPlanRefund />} />

                        {/* State Land (국유지) */}
                        <Route path="/stateLandInfo" element={<StateLandInfo />} />
                        <Route path="/stateLandUserFilter" element={<StateLandUserFilter />} />

                        {/* Owner Check / Coupon */}
                        <Route path="/ownerCheck/payment" element={<OwnerCheckChargePayment />} />
                        <Route path="/successCoupon" element={<CouponRegistrationCompleted />} />

                        {/* MissgoAI */}
                        <Route path="/missgoAI" element={<MissgoAI />} />
                        <Route path="/missgoAI/viewer" element={<MissgoAIPdfViewer />} />
                        <Route path="/missgoAI/deep" element={<MissgoAIDeep />} />
                        <Route path="/missgoAI/deep/payment" element={<MissgoAIDeepPayment />} />

                        {/* Events */}
                        <Route path="/event/aiDividendEvent" element={<AiDividendEvent />} />
                        <Route path="/event/datamapNewFnEvent" element={<DatamapNewFnEvent />} />
                        <Route path="/event/ownerCheckServiceEvent" element={<OwnerCheckServiceEvent />} />
                        <Route path="/event/missgoAiBannerEvent" element={<MissgoAiBannerEvent />} />
                        <Route path="/event/AiChatBotBannerEvent" element={<AiChatBotBannerEvent />} />
                        <Route path="/event/earlyBirdVip50" element={<EarlyBirdVip50Event />} />
                        <Route path="/event/AdvertisementEvent" element={<AdvertisementEvent />} />
                        <Route path="/event/FiveFreeEvent" element={<FiveFreeEvent />} />
                        <Route path="/event/ArchitectEvent" element={<ArchitectEvent />} />
                        <Route path="/event/accompanying" element={<Accompanying />} />
                        <Route path="/event/missgoAIDeep" element={<MissgoAIDeep />} />

                        {/* Others */}
                        <Route path="/speechShorts" element={<SpeechShorts />} />
                        <Route path="/introduction/IosDatamap" element={<IosDatamap />} />
                        <Route path="/customerService/inquiry" element={<CustomerServiceInquiry />} />
                        <Route path="/three" element={<ThreePage />} />
                        <Route path="/BuildingStatusTable" element={<BuildingStatusTablePage />} />
                        <Route path="/smallRentTable" element={<SmallRentTablePage />} />
                        <Route path="/StatusItem" element={<StatusItem />} />
                        <Route path="/redirectStore" element={<StoreRedirect />} />
                        <Route path="/sampleBidList" element={<SampleBidList />} />
                        <Route path="/table" element={<TestPage />} />
                        <Route path="/internaltablepage" element={<IntrenalTablePage />} />
                        <Route path="/aibottomsheet" element={<TestAiPage />} />
                        <Route
                            path="/auctionAgent/chatting"
                            element={
                                <ChattingRoomProvider>
                                    <SendBirdChatProvider>
                                        <AuctionAgentChatting />
                                    </SendBirdChatProvider>
                                </ChattingRoomProvider>
                            }
                        />
                    </>
                ) : (
                    <>
                        <Route path="/speechShorts" element={<SpeechShorts />} />
                        <Route path="/registerCoupon" element={<RegisterCouponIOS />} />
                        <Route
                            path="*"
                            element={
                                <StyledLottie>
                                    <Lottie className="lottie" animationData={loading} />
                                </StyledLottie>
                            }
                        />
                    </>
                )}
            </Routes>
        </>
    );
}

export default Router;

const StyledLottie = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh; /* [3] */
    height: calc(var(--vh, 1vh) * 100); /* [4] */
    width: 100%;
    & > .lottie {
        width: 50%;
    }
`;
